import { useState } from "react";
import { Zap, TrendingDown, ArrowRight, Loader2, ShieldCheck, Award } from "lucide-react";
import { trackComparisonClick } from "@/lib/api";
import { toast } from "sonner";

const fmtEur = (n) =>
  new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

/**
 * SmartOverstapBanner — prominent CTA at the top of an unlocked report.
 * Directs the user to Pricewise (Daisycon affiliate) with their data pre-filled.
 * Only renders when there's a meaningful savings opportunity (>= €50/year).
 */
export default function SmartOverstapBanner({ analysisId, possibleSavings = 0 }) {
  const [loading, setLoading] = useState(false);
  if (!possibleSavings || possibleSavings < 50) return null;

  const onOverstap = async () => {
    setLoading(true);
    try {
      const r = await trackComparisonClick({ provider_id: "pricewise", analysis_id: analysisId });
      if (r.url) {
        window.open(r.url, "_blank", "noopener,noreferrer");
      }
    } catch {
      toast.error("Doorsturen mislukt — probeer opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="mb-8 relative overflow-hidden rounded-2xl"
      data-testid="smart-overstap-banner"
    >
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-800" />
      <div aria-hidden className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-amber-300/20 blur-3xl" />
      <div aria-hidden className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-blue-400/20 blur-3xl" />

      <div className="relative p-6 sm:p-8 lg:p-10">
        <div className="grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center gap-1.5 bg-amber-300/15 border border-amber-200/30 text-amber-100 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
              <Zap className="h-3 w-3" />
              Slimme overstap
            </div>
            <h3 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Bespaar <span className="text-amber-300">{fmtEur(possibleSavings)}</span> per jaar
            </h3>
            <p className="mt-3 text-emerald-50 leading-relaxed max-w-xl">
              Op basis van jouw contract. Stap direct over via <strong>Pricewise</strong> — ACM-erkend,
              je postcode en verbruik staan al ingevuld. Geen account aanmaken.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-emerald-100">
              <span className="inline-flex items-center gap-1.5">
                <Award className="h-3.5 w-3.5 text-amber-300" />
                ACM Keurmerk Vergelijken
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-amber-300" />
                Geen verborgen kosten
              </span>
              <span className="inline-flex items-center gap-1.5">
                <TrendingDown className="h-3.5 w-3.5 text-amber-300" />
                Eigen overstapservice
              </span>
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-start lg:justify-end">
            <button
              onClick={onOverstap}
              disabled={loading}
              className="group w-full lg:w-auto inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-xl px-7 py-4 text-base sm:text-lg shadow-2xl shadow-emerald-900/40 transition-all hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="smart-overstap-cta"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Doorsturen naar Pricewise...
                </>
              ) : (
                <>
                  Stap nu over &amp; bespaar
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </div>
        </div>
        <p className="mt-5 text-[11px] text-emerald-100/80 max-w-2xl">
          EnergieKontract ontvangt een commissie wanneer je via Pricewise overstapt. Dit beïnvloedt de
          prijs die jij betaalt niet — Pricewise is ACM-erkend en onafhankelijk getoetst.
        </p>
      </div>
    </section>
  );
}
